import React, { useState, useRef } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch, apiDownload } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { API_DOCS } from './AggiornaPrezziPage.api';

interface RisultatoUpload {
  totaleRighe: number;
  righeAggiornate: number;
  righeScartate: number;
  righeErrore: number;
  logTesto: string;
  fatalError: string | null;
}

export default function AggiornaPrezziPage() {
  const { societaId: filterSocietaId, pronta } = useFiltroSocieta();

  const [file, setFile]               = useState<File | null>(null);
  const [importing, setImporting]     = useState(false);
  const [risultato, setRisultato]     = useState<RisultatoUpload | null>(null);
  const [logVisibile, setLogVisibile] = useState(false);
  const inputFileRef = useRef<HTMLInputElement>(null);

  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  const handleScaricaTemplate = async () => {
    try {
      // @api GET /api/anagrafiche/aggiorna-prezzi/template -> file xlsx
      await apiDownload(
        `/api/anagrafiche/aggiorna-prezzi/template?_t=${Date.now()}`,
        'template_aggiorna_prezzi.xlsx'
      );
    } catch (err: any) {
      showToast('Errore download template: ' + err.message, 'error');
    }
  };

  const handleImport = async () => {
    if (!file) return;
    if (!filterSocietaId) {
      showToast('Seleziona prima una società dalla barra in alto', 'error');
      return;
    }
    setImporting(true);
    setRisultato(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('societaId', String(filterSocietaId));
      // @api POST /api/anagrafiche/aggiorna-prezzi/upload (multipart: file, societaId) -> {"success":true,"data":{"totaleRighe":"number","righeAggiornate":"number","righeScartate":"number","righeErrore":"number","logTesto":"string","fatalError":"string|null"}}
      const res = await apiFetch<{ success: boolean; data: RisultatoUpload }>(
        '/api/anagrafiche/aggiorna-prezzi/upload',
        { method: 'POST', body: formData }
      );
      setRisultato(res.data);
      setFile(null);
      if (inputFileRef.current) inputFileRef.current.value = '';
      if (res.data.fatalError) {
        showToast(`Errore: ${res.data.fatalError}`, 'error');
      } else {
        showToast(
          `Aggiornamento completato — ${res.data.righeAggiornate} prezzi aggiornati, ${res.data.righeScartate} scartati`,
          'success'
        );
      }
    } catch (err: any) {
      showToast(err.message || 'Errore durante l\'aggiornamento', 'error');
    } finally {
      setImporting(false);
    }
  };

  const stileRisultato = (() => {
    if (!risultato) return '';
    if (risultato.fatalError || risultato.righeErrore > 0) {
      return risultato.righeAggiornate > 0 ? 'warning' : 'error';
    }
    return 'success';
  })();

  return (
    <Layout>
      {toast && (
        <div className={`amministrazione-toast ${
          toast.type === 'success' ? 'amministrazione-toast-success' : 'amministrazione-toast-error'
        }`}>
          {toast.msg}
        </div>
      )}

      <div className="aggiorna-prezzi-page">
        <div className="bop-page-header">
          <h1 className="bop-page-title">Aggiorna prezzi da xls</h1>
        </div>

        {/* SEZIONE — Descrizione */}
        <details className="intc-import-help intc-rules-help" open>
          <summary className="intc-import-help-summary">
            <span className="intc-import-help-icon intc-import-help-icon--warn" aria-hidden="true">!</span>
            <span className="intc-import-help-title">Come funziona</span>
            <span className="intc-import-help-toggle">apri / chiudi</span>
          </summary>
          <div className="intc-import-help-body">
            <p className="intc-import-help-lead">
              Questa funzione permette di <strong>aggiornare in modo massivo solo i prezzi</strong> degli
              articoli già presenti nell'anagrafica della società selezionata. Non crea nuovi articoli
              e non modifica gli altri campi (descrizione, marca, collezione, categorie, ecc.).
            </p>
            <ul className="intc-import-help-list intc-rules-list">
              <li>
                Il file Excel deve avere <strong>due sole colonne</strong>: <code>codice</code> e <code>prezzo_listino</code>.
              </li>
              <li>
                Per ogni riga, il sistema cerca l'articolo per <strong>codice</strong> nella società
                attiva. Se trovato, ne aggiorna il <strong>prezzo di listino</strong> (campo
                "Prezzo standard") e ricalcola il prezzo di vendita in base al convertitore.
              </li>
              <li>
                I codici <strong>non presenti</strong> in anagrafica vengono semplicemente <strong>scartati</strong>:
                non vengono creati nuovi articoli e non viene generato un errore bloccante.
              </li>
              <li>
                Le righe con prezzo non valido (vuoto, non numerico o negativo) vengono conteggiate
                come <strong>errore</strong> ma non bloccano l'elaborazione.
              </li>
              <li>
                A differenza di <em>Import Listini</em>, questa funzione non crea righe in
                ListinoFornitore e non tiene uno storico. È pensata solo per allineare velocemente
                i prezzi dell'anagrafica articoli a partire da un listino sintetico.
              </li>
              <li>
                <strong>Suggerimento:</strong> scarica il template, compila le due colonne e carica il file.
              </li>
            </ul>
          </div>
        </details>

        {/* SEZIONE — Pannello aggiornamento */}
        <div className="bop-card">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Carica file di aggiornamento prezzi</h2>
          </div>
          <div className="bop-card__body">
            {!filterSocietaId && pronta && (
              <div className="listini-avviso-societa">
                Seleziona una società dalla barra in alto per aggiornare i prezzi.
              </div>
            )}

            <div className="listini-azioni-form">
              <button className="bop-btn-secondary" onClick={handleScaricaTemplate}>
                ⬇ Scarica Template
              </button>
              <div
                className={`listini-upload-area ${file ? 'has-file' : ''}`}
                onClick={() => inputFileRef.current?.click()}
              >
                {file ? `📎 ${file.name}` : 'Clicca o trascina qui il file Excel (.xlsx)'}
                <input
                  ref={inputFileRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="listini-input-file-hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </div>
              <button
                className="bop-btn-primary"
                onClick={handleImport}
                disabled={!file || importing || !filterSocietaId}
              >
                {importing ? '⏳ Aggiornamento in corso...' : '▶ Avvia aggiornamento'}
              </button>
            </div>
          </div>
        </div>

        {/* SEZIONE — Risultato */}
        {risultato && (
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">Risultato</h2>
            </div>
            <div className="bop-card__body">
              <div className={`listini-risultato ${stileRisultato}`}>
                {risultato.fatalError ? (
                  <p>
                    <strong>Errore bloccante:</strong> {risultato.fatalError}
                  </p>
                ) : (
                  <p>
                    Elaborazione completata su {risultato.totaleRighe} righe.
                  </p>
                )}
                <div className="listini-contatori">
                  <div className="listini-contatore">
                    <span className="listini-contatore-valore">{risultato.totaleRighe}</span>
                    <span className="listini-contatore-label">Righe totali</span>
                  </div>
                  <div className="listini-contatore">
                    <span className="listini-contatore-valore">{risultato.righeAggiornate}</span>
                    <span className="listini-contatore-label">Aggiornate</span>
                  </div>
                  <div className="listini-contatore">
                    <span className="listini-contatore-valore">{risultato.righeScartate}</span>
                    <span className="listini-contatore-label">Scartate (codice non presente)</span>
                  </div>
                  <div className="listini-contatore">
                    <span className="listini-contatore-valore">{risultato.righeErrore}</span>
                    <span className="listini-contatore-label">Errori</span>
                  </div>
                </div>
                <div className="listini-risultato-azioni">
                  <button
                    className="bop-btn-secondary"
                    onClick={() => setLogVisibile(v => !v)}
                  >
                    {logVisibile ? 'Nascondi log' : 'Mostra log dettagliato'}
                  </button>
                </div>
                {logVisibile && (
                  <pre className="aggiorna-prezzi-log">{risultato.logTesto}</pre>
                )}
              </div>
            </div>
          </div>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
